import React, { useState, useMemo } from 'react';
import { 
  MapPin, Users, Plus, ShieldAlert, User, 
  CheckCircle2, Trash2, X, Edit3, ShieldCheck
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const GestionSalones = () => {
  // 1. Consumimos datos globales reales
  const { ninos, salones, setSalones, maestrosGlobal } = useGlobal();

  const [modoCreacion, setModoCreacion] = useState(false);
  const [grupoEnEdicion, setGrupoEnEdicion] = useState(null);
  const [formGrupo, setFormGrupo] = useState({ nombre: '', tipo: 'Salón Físico', maestro: '', ubicacion: '' });

  // 2. LÓGICA DE CONTEO DINÁMICO (Reactividad Total)
  const salonesConCapacidad = useMemo(() => {
    return salones.map(salon => {
      const conteoReal = ninos.filter(n => n.salon === salon.nombre && n.estatus === 'en_salon').length;
      return { ...salon, ninos: conteoReal };
    });
  }, [ninos, salones]);

  // --- LÓGICA DE GESTIÓN ---
  const prepararEdicion = (grupo) => {
    setGrupoEnEdicion(grupo.id);
    setFormGrupo({
      nombre: grupo.nombre,
      tipo: grupo.tipo,
      maestro: grupo.maestro,
      ubicacion: grupo.ubicacion
    });
    setModoCreacion(true);
  };

  const guardarGrupo = () => {
    if(!formGrupo.nombre) return;

    const nombreFormateado = formGrupo.nombre.toUpperCase();

    if (grupoEnEdicion) {
      setSalones(salones.map(g => 
        g.id === grupoEnEdicion ? { ...g, ...formGrupo, nombre: nombreFormateado } : g
      ));
    } else {
      setSalones([...salones, {
        ...formGrupo,
        id: Date.now(),
        nombre: nombreFormateado,
        ubicacion: formGrupo.ubicacion || (formGrupo.tipo === 'Salón Físico' ? 'Aula Asignada' : 'Área Abierta'),
        estado: 'ok'
      }]);
    }
    cerrarFormulario();
  };

  const cerrarFormulario = () => {
    setModoCreacion(false);
    setGrupoEnEdicion(null);
    setFormGrupo({ nombre: '', tipo: 'Salón Físico', maestro: '', ubicacion: '' });
  };

  const eliminarGrupo = (id) => {
    if(window.confirm("¿CERRAR SALÓN? Se desvinculará de los reportes actuales y el staff perderá acceso al tablero de este grupo.")) {
      setSalones(salones.filter(g => g.id !== id));
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-10 animate-in fade-in duration-500 pb-20 px-4 text-left font-sans">
      
      {/* HEADER OPERATIVO */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b-4 border-slate-100 pb-8">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full shadow-lg">
            <ShieldCheck size={18} className="text-amber-400" />
            <span className="text-[10px] font-black uppercase tracking-widest italic">Infraestructura IgleKids</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 italic tracking-tighter uppercase leading-none">Salones</h2>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_#10b981]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
              {salones.length} Estaciones de servicio activas
            </span>
          </div>
        </div>

        {!modoCreacion && (
          <button 
            onClick={() => setModoCreacion(true)}
            className="group bg-[#0A2440] text-white px-10 py-5 rounded-[2.5rem] flex items-center gap-4 shadow-2xl hover:bg-indigo-600 transition-all active:scale-95 border-b-8 border-slate-800"
          >
            <Plus size={28} className="text-emerald-400 group-hover:rotate-90 transition-transform" />
            <span className="font-black text-xs uppercase tracking-widest">Abrir Nuevo Grupo</span>
          </button>
        )}
      </div>

      {/* FORMULARIO DE APERTURA / EDICIÓN */}
      {modoCreacion && (
        <div className="max-w-3xl mx-auto bg-white p-10 md:p-14 rounded-[4rem] shadow-2xl border-4 border-slate-50 space-y-10 animate-in zoom-in-95 duration-300 relative overflow-hidden">
          <div className="flex justify-between items-center text-left">
            <div className="space-y-1">
              <h3 className="text-2xl font-black uppercase italic tracking-tighter text-slate-900">
                {grupoEnEdicion ? 'Configurar Aula' : 'Apertura de Estación'}
              </h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">Gestión de recursos y liderazgo</p>
            </div>
            <button onClick={cerrarFormulario} className="p-3 bg-slate-100 rounded-full text-slate-400 hover:text-rose-600 transition-all">
              <X size={24}/>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-6 text-left">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 italic">Identificador</label>
                <input 
                  type="text" placeholder="EJ. CUNA A"
                  className="w-full p-6 bg-slate-50 rounded-[2.2rem] border-2 border-transparent focus:border-indigo-500 text-xl font-black text-slate-800 outline-none transition-all uppercase shadow-inner"
                  value={formGrupo.nombre}
                  onChange={(e) => setFormGrupo({...formGrupo, nombre: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 italic">Modo de Operación</label>
                <div className="grid grid-cols-2 gap-3">
                  {['Salón Físico', 'Grupo Abierto'].map(t => (
                    <button 
                      key={t} onClick={() => setFormGrupo({...formGrupo, tipo: t})}
                      className={`py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest border-4 transition-all ${
                        formGrupo.tipo === t ? 'bg-indigo-600 text-white border-indigo-700 shadow-lg' : 'bg-slate-50 text-slate-300 border-transparent'
                      }`}
                    >
                      {t === 'Salón Físico' ? 'Aula' : 'Abierto'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6 text-left">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 italic">Líder de Salón</label>
                <select 
                  className="w-full p-6 bg-slate-50 rounded-[2.2rem] border-2 border-transparent focus:border-indigo-500 text-sm font-black text-slate-600 outline-none appearance-none shadow-inner"
                  value={formGrupo.maestro}
                  onChange={(e) => setFormGrupo({...formGrupo, maestro: e.target.value})}
                >
                  <option value="">Asignar Staff...</option>
                  {(maestrosGlobal || []).filter(m => m.estatus === 'Activo').map(p => (
                    <option key={p.id} value={p.nombre}>{p.nombre.toUpperCase()}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 italic">Ubicación Geográfica</label>
                <input 
                  type="text" placeholder="Ej. Edificio B - Nivel 1"
                  className="w-full p-6 bg-slate-50 rounded-[2.2rem] border-2 border-transparent focus:border-indigo-500 font-bold text-slate-800 outline-none shadow-inner"
                  value={formGrupo.ubicacion}
                  onChange={(e) => setFormGrupo({...formGrupo, ubicacion: e.target.value})}
                />
              </div>
            </div>
          </div>

          <button 
            onClick={guardarGrupo}
            className="w-full py-8 bg-emerald-500 text-white rounded-[3.5rem] font-black text-sm uppercase tracking-[0.3em] shadow-2xl hover:bg-emerald-600 active:scale-95 transition-all flex items-center justify-center gap-4 border-b-8 border-emerald-700"
          >
            {grupoEnEdicion ? 'Actualizar Estación' : 'Apertura Oficial'} <CheckCircle2 size={24} />
          </button>
        </div>
      )}

      {/* MONITOR DE SALONES EN TIEMPO REAL */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {salonesConCapacidad.length === 0 ? (
          <div className="col-span-full py-32 text-center bg-slate-100 rounded-[4rem] border-4 border-dashed border-slate-200 opacity-40">
             <MapPin size={80} className="mx-auto mb-4 text-slate-400" />
             <p className="font-black uppercase tracking-widest text-slate-500 italic">No hay operaciones activas hoy</p>
          </div>
        ) : (
          salonesConCapacidad.map((grupo) => (
            <div 
              key={grupo.id} 
              className={`bg-white rounded-[4rem] p-10 shadow-sm border-4 transition-all flex flex-col justify-between hover:shadow-2xl hover:border-indigo-100 group relative overflow-hidden ${
                grupo.ninos > 20 ? 'border-rose-100 bg-rose-50/10' : 'border-slate-50'
              }`}
            >
              <div className="space-y-8 relative z-10 text-left">
                <div className="flex justify-between items-start">
                  <div className="space-y-3">
                    <span className={`text-[9px] font-black uppercase tracking-[0.2em] px-5 py-2 rounded-full border italic ${
                      grupo.tipo === 'Salón Físico' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                    }`}>
                      {grupo.tipo}
                    </span>
                    <h3 className="text-3xl font-black italic text-slate-900 tracking-tighter uppercase leading-none">{grupo.nombre}</h3>
                    <div className="flex items-center gap-2 text-slate-400 font-bold">
                      <MapPin size={14} />
                      <span className="text-[10px] uppercase tracking-widest italic">{grupo.ubicacion}</span>
                    </div>
                  </div>
                  
                  <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-[2.8rem] shadow-inner border-4 transition-all ${
                    grupo.ninos > 20 ? 'bg-rose-500 text-white border-rose-400 animate-pulse' : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                  }`}>
                    <span className="text-4xl font-black leading-none tabular-nums">{grupo.ninos}</span>
                    <Users size={16} className="mt-1" />
                  </div>
                </div>

                <div className="bg-white rounded-[2.5rem] p-6 border-2 border-slate-100 shadow-sm group-hover:border-indigo-50 transition-colors">
                  <div className="flex items-center gap-4 text-left">
                    <div className="w-14 h-14 bg-indigo-50 rounded-[1.5rem] flex items-center justify-center text-indigo-500 shadow-inner">
                      <User size={28} />
                    </div>
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 italic">Responsable de Aula</p>
                      <p className="text-base font-black text-slate-800 leading-none uppercase italic">{grupo.maestro || 'POR ASIGNAR'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACCIONES DE ESTACIÓN */}
              <div className="grid grid-cols-2 gap-4 mt-10 relative z-10">
                <button 
                  onClick={() => prepararEdicion(grupo)}
                  className="py-5 bg-slate-900 text-white rounded-3xl font-black text-[9px] uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all flex items-center justify-center gap-2 shadow-xl active:scale-95"
                >
                  <Edit3 size={14} /> Configurar
                </button>
                <button 
                  onClick={() => eliminarGrupo(grupo.id)}
                  className="py-5 bg-white text-rose-500 rounded-3xl font-black text-[9px] uppercase tracking-[0.2em] hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center gap-2 border-2 border-rose-50 shadow-md"
                >
                  <Trash2 size={14} /> Cerrar
                </button>
              </div>
              
              <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-slate-50 rounded-full opacity-50 group-hover:bg-indigo-50 transition-all" />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GestionSalones;